import { NavLink } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  LayoutDashboard,
  Map,
  CalendarDays,
  Clock,
  Users,
  ClipboardList,
  Settings,
  ChevronLeft,
  UtensilsCrossed,
  Phone,
  Globe,
  ShoppingCart,
  Package,
  TicketPercent,
  Network,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { useAppStore } from '@/lib/store/useAppStore'
import { useNavCounts } from '@/hooks/useNavCounts'

function Badge({ count }: { count: number }) {
  if (count <= 0) return null
  return (
    <span className="ml-auto flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-[var(--color-accent-amber)] px-1.5 text-[10px] font-bold text-white leading-none">
      {count > 9 ? '9+' : count}
    </span>
  )
}

type NavItem = {
  to: string
  icon: React.ElementType
  label: string
  count: number
}

function SidebarLink({ item, collapsed }: { item: NavItem; collapsed: boolean }) {
  const { to, icon: Icon, label, count } = item

  return (
    <NavLink
      to={to}
      title={collapsed ? label : undefined}
      className={({ isActive }) =>
        cn(
          'relative flex items-center gap-3 rounded-[var(--radius-md)] px-3 py-2.5 text-sm font-medium transition-all duration-200',
          isActive
            ? 'bg-[var(--color-accent-amber-dim)] text-[var(--color-accent-amber)]'
            : 'text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-elevated)]',
        )
      }
    >
      {() => (
        <>
          <Icon className="h-5 w-5 shrink-0" />
          {!collapsed && <span className="flex-1">{label}</span>}
          {!collapsed && <Badge count={count} />}
          {collapsed && count > 0 && (
            <span className="absolute -top-1 -right-1 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-[var(--color-accent-amber)] px-1 text-[9px] font-bold text-white leading-none">
              {count > 9 ? '9+' : count}
            </span>
          )}
        </>
      )}
    </NavLink>
  )
}

export function Sidebar() {
  const { t } = useTranslation()
  const { sidebarCollapsed, toggleSidebar, featureSheAnswers, featureCustomerPage, featurePOS } = useAppStore()
  const counts = useNavCounts()

  const mainNavItems: NavItem[] = [
    { to: '/dashboard', icon: LayoutDashboard, label: t('nav.dashboard'), count: 0 },
  ]

  const serviceNavItems: NavItem[] = [
    { to: '/floor-plan', icon: Map, label: t('nav.floorPlan'), count: 0 },
    { to: '/bookings', icon: CalendarDays, label: t('nav.bookings'), count: counts.bookings },
    { to: '/orders', icon: ClipboardList, label: t('nav.orders'), count: counts.collections },
    { to: '/customers', icon: Users, label: t('nav.customers'), count: 0 },
  ]

  const onlineNavItems = [
    featureSheAnswers && { to: '/she-answers', icon: Phone, label: t('nav.sheAnswers'), count: 0 },
    featureCustomerPage && { to: '/customer-page', icon: Globe, label: t('nav.customerPage'), count: 0 },
    featureCustomerPage && { to: '/coupons', icon: TicketPercent, label: 'Coupons', count: 0 },
    { to: '/ordevo-network', icon: Network, label: t('nav.ordevoNetwork'), count: 0 },
  ].filter(Boolean) as NavItem[]

  const posNavItems = [
    featurePOS
      ? { to: '/pos', icon: ShoppingCart, label: t('nav.pos'), count: counts.pos }
      : { to: '/pos', icon: UtensilsCrossed, label: t('nav.menu'), count: 0 },
    featurePOS && { to: '/collections', icon: Package, label: t('nav.collections'), count: counts.collections },
  ].filter(Boolean) as NavItem[]

  const setupNavItems: NavItem[] = [
    { to: '/opening-hours', icon: Clock, label: t('nav.openingHours'), count: 0 },
  ]

  const settingsNavItems: NavItem[] = [
    { to: '/settings', icon: Settings, label: t('nav.settings'), count: 0 },
  ]

  const navSections = [
    { label: 'Main', items: mainNavItems },
    { label: 'Service', items: serviceNavItems },
    { label: 'Online', items: onlineNavItems },
    { label: 'POS', items: posNavItems },
    { label: 'Setup', items: setupNavItems },
    { label: 'Settings', items: settingsNavItems },
  ].filter((section) => section.items.length > 0)

  return (
    <motion.aside
      animate={{ width: sidebarCollapsed ? 64 : 240 }}
      transition={{ duration: 0.2, ease: [0.25, 0.46, 0.45, 0.94] }}
      className="hidden md:flex fixed left-0 top-0 bottom-0 z-40 flex-col bg-[var(--color-bg-surface)] border-r border-[var(--color-border-subtle)]"
    >
      <div className="flex items-center gap-3 px-4 h-16 border-b border-[var(--color-border-subtle)]">
        <div className="flex h-9 w-9 items-center justify-center rounded-[var(--radius-md)] bg-[var(--color-accent-amber-dim)]">
          <UtensilsCrossed className="h-5 w-5 text-[var(--color-accent-amber)]" />
        </div>
        {!sidebarCollapsed && (
          <motion.span
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="font-display text-lg font-semibold text-[var(--color-text-primary)]"
          >
            Ordevo
          </motion.span>
        )}
      </div>

      <nav className="flex-1 px-2 py-4 overflow-y-auto">
        {navSections.map((section, index) => (
          <div key={section.label} className={cn(index > 0 && 'mt-4')}>
            {sidebarCollapsed ? (
              index > 0 && <div className="mx-2 mb-2 border-t border-[var(--color-border-subtle)]" />
            ) : (
              <div className={cn('px-3 pb-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--color-text-muted)]', index === 0 ? 'pt-0' : 'pt-1')}>
                {section.label}
              </div>
            )}
            <div className="space-y-1">
              {section.items.map((item) => (
                <SidebarLink key={item.to} item={item} collapsed={sidebarCollapsed} />
              ))}
            </div>
          </div>
        ))}
      </nav>

      <div className="p-2 border-t border-[var(--color-border-subtle)]">
        <button
          type="button"
          onClick={toggleSidebar}
          aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          className="flex w-full items-center justify-center rounded-[var(--radius-md)] py-2 text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-elevated)] transition-colors"
        >
          <ChevronLeft
            className={cn(
              'h-4 w-4 transition-transform duration-200',
              sidebarCollapsed && 'rotate-180',
            )}
          />
        </button>
      </div>
    </motion.aside>
  )
}
